import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Robot, Presentation, Flask, Envelope, ArrowUpRight, Lightning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const ICONS = { robot: Robot, presentation: Presentation, flask: Flask, envelope: Envelope };

export default function Build() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/simulator/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-500">…</div>;

  return (
    <div className="min-h-screen pb-32 relative overflow-hidden" data-testid="build-screen">
      {/* Geometric mesh bg */}
      <div className="absolute inset-0 mesh-bg" />
      <div className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "32px 32px" }}
      />

      <div className="relative px-6 pt-12 pb-4">
        <p className="text-xs tracking-widest uppercase text-zinc-500">Founder OS</p>
        <h1 className="font-display text-4xl font-light tracking-tight">Build the thing.</h1>
      </div>

      {/* Idea card */}
      <div className="relative px-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass-strong rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-indigo-500/15 blur-3xl" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Active Idea · {data.idea.stage}</div>
          <h2 className="mt-1 font-display text-3xl font-light">{data.idea.title}</h2>
          <p className="text-zinc-400 text-sm">{data.idea.tagline}</p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <Metric label="Validation" value={data.idea.validation_signals} accent="#818CF8" />
            <Metric label="Users talked to" value={data.idea.users_talked_to} accent="#60A5FA" />
            <ReadinessGauge value={data.investor_readiness} />
          </div>
        </motion.div>
      </div>

      {/* Tools */}
      <div className="relative mt-6 px-6">
        <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Founder tools</h3>
        <div className="grid grid-cols-2 gap-3">
          {data.tools.map((t) => {
            const Ic = ICONS[t.icon] || Robot;
            return (
              <motion.button
                key={t.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileTap={{ scale: 0.97 }}
                data-testid={`tool-${t.id}`}
                className="glass rounded-2xl p-4 text-left relative overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full bg-indigo-500/10 blur-2xl" />
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-2">
                  <Ic size={18} weight="fill" className="text-indigo-300" />
                </div>
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-snug">{t.desc}</div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Challenges */}
      <div className="relative mt-8 px-6">
        <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Founder challenges</h3>
        <div className="space-y-2">
          {data.challenges.map((c) => (
            <div key={c.id} className="glass rounded-2xl p-4" data-testid={`fchallenge-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">{c.title}</div>
                <div className="text-[11px] text-zinc-500 font-mono-jb">{Math.round(c.progress * 100)}%</div>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-violet-300" style={{ width: `${c.progress * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cofounder CTA */}
      <div className="relative mt-8 px-6">
        <Link to="/chat/m_startup" className="block glass-strong rounded-3xl p-5 active:scale-[0.99] transition" data-testid="ai-cofounder-cta">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center">
              <Robot size={22} weight="fill" className="text-indigo-300" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">Brainstorm with your AI Cofounder</div>
              <div className="text-[11px] text-zinc-500">Stress-test ideas, draft memos, run scenarios</div>
            </div>
            <ArrowUpRight size={16} className="text-zinc-300" />
          </div>
        </Link>
      </div>
    </div>
  );
}

const Metric = ({ label, value, accent }) => (
  <div className="rounded-2xl bg-white/[0.03] p-3 relative overflow-hidden">
    <div className="absolute -bottom-4 -right-4 w-14 h-14 rounded-full blur-xl" style={{ background: accent, opacity: 0.18 }} />
    <Lightning size={12} weight="fill" style={{ color: accent }} />
    <div className="mt-1 font-display text-2xl font-light">{value}</div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
  </div>
);

const ReadinessGauge = ({ value }) => {
  const r = 22, c = 2 * Math.PI * r;
  return (
    <div className="rounded-2xl bg-white/[0.03] p-3 flex flex-col items-center relative overflow-hidden">
      <svg width="60" height="60" viewBox="0 0 60 60" className="-rotate-90">
        <circle cx="30" cy="30" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="4" fill="none" />
        <circle cx="30" cy="30" r={r} stroke="#FBBF24" strokeWidth="4" strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} fill="none" strokeLinecap="round" />
      </svg>
      <div className="-mt-9 font-display text-sm">{value}</div>
      <div className="mt-3 text-[8px] uppercase tracking-widest text-zinc-500">Investor</div>
    </div>
  );
};
